// Biome definitions — the single per-biome surface. Adding a biome is one
// BIOMES row (bands come from the radial table below; new numeric knobs, if
// any, go in T). No system branches on a biome id directly; systems read
// BIOMES[variant].* and the geometry helpers.
//
// Numeric knobs live in T (so the Tweakpane can sweep them live); the table
// exposes them through accessor functions rather than copying values.

import type { Entity } from './components'
import type { SpeciesId } from './species'
import { worldDistanceAt } from './dungeon/depth-field'
import { T } from './tunables'

export type BiomeId = 'shallow' | 'deep' | 'mushroom'
type Rgb = [number, number, number]

export interface PredatorMods {
  radiusMult: number
  hpMult: number
  damageMult: number
  windupMult: number
}

export interface BiomeDef {
  /** Creature-shader "descent" flavor in [0,1] (0 = shallow, 1 = deep+). */
  shaderVariant: number
  /** Player tier at which this biome stops taxing damage (soft gate). */
  homeTier: number
  /** Damage multiplier applied to players below homeTier. */
  underTierDamageMult(): number
  microbeTint(): Rgb
  predatorTint(): Rgb
  predatorMods(): PredatorMods
  microbeWeights: [SpeciesId, number][]
  predatorWeights: [SpeciesId, number][]
  /** Background wash multiplier (paper × wash) — [1,1,1] = none. */
  wash: Rgb
  /** Background ink tint for this biome's motes/lines. */
  inkShift: Rgb
  /** Zone name shown on the depth rail. */
  displayName: string
}

export const BIOMES: Record<BiomeId, BiomeDef> = {
  shallow: {
    shaderVariant: 0,
    homeTier: 1,
    underTierDamageMult: () => 1,
    microbeTint: () => [0.5 + Math.random() * 0.4, 0.7 + Math.random() * 0.3, 1.0],
    predatorTint: () => T.predator.shallowTint,
    predatorMods: () => ({ radiusMult: 1, hpMult: 1, damageMult: 1, windupMult: 1 }),
    microbeWeights: [
      ['ciliate',    36],
      ['flagellate', 22],
      ['spore',      18],
      ['bristled',   14],
      ['rod',        12],
      ['spiral',     10],
      ['plantCell',   6], // vaults are rarer, higher-value
    ],
    predatorWeights: [
      ['pseudopod', 60],
      ['spiked',    40],
    ],
    wash: [1, 1, 1],           // no wash — bright paper
    inkShift: [1, 1, 1],       // default ink
    displayName: 'shallow pool',
  },
  deep: {
    shaderVariant: 1,
    homeTier: 2,
    underTierDamageMult: () => T.damage.deepTier1Mult,
    // Phase 9 — Luminous microbe palette. Cooler than shallow, with a violet
    // bias to set up the descent toward Heck without leaving the picture-book
    // register. The shader brightens the organelles for a faint inner glow.
    microbeTint: () => [0.45 + Math.random() * 0.25, 0.55 + Math.random() * 0.25, 0.85 + Math.random() * 0.15],
    predatorTint: () => T.predator.deepTint,
    predatorMods: () => ({
      radiusMult: T.predator.deepRadiusMult,
      hpMult: T.predator.deepHpMult,
      damageMult: T.predator.deepDamageMult,
      windupMult: T.predator.deepWindupMult,
    }),
    microbeWeights: [
      ['bristled',   28],
      ['flagellate', 26],
      ['spore',      22],
      ['ciliate',    14],
      ['rod',        12],
      ['spiral',     10],
      ['plantCell',   6],
    ],
    predatorWeights: [
      ['spiked',    65],
      ['pseudopod', 35],
    ],
    // Warm violet-brown dusk (matches background.slang's former deepWash/deepInk).
    wash: [0.34, 0.30, 0.44],
    inkShift: [0.55, 0.18, 0.16],
    displayName: 'deep water',
  },
  // Tier-3 "forest floor": earthy, fungal. Harder than deep, still whimsical.
  mushroom: {
    shaderVariant: 1,
    homeTier: 3,
    underTierDamageMult: () => T.damage.mushroomTierMult,
    // Earthy creatures: warm ochre-browns with a faint spore-lavender lift.
    microbeTint: () => [0.62 + Math.random() * 0.20, 0.48 + Math.random() * 0.16, 0.38 + Math.random() * 0.16],
    predatorTint: () => T.predator.mushroomTint,
    predatorMods: () => ({
      radiusMult: T.predator.mushroomRadiusMult,
      hpMult: T.predator.mushroomHpMult,
      damageMult: T.predator.mushroomDamageMult,
      windupMult: T.predator.mushroomWindupMult,
    }),
    microbeWeights: [
      ['toadstool',  34], // the signature mushroom-floor food
      ['spore',      22],
      ['bristled',   16],
      ['rod',        14],
      ['plantCell',  10],
      ['spiral',      8],
    ],
    predatorWeights: [
      ['spiked',    55],
      ['pseudopod', 45],
    ],
    // Warm soil-brown ground + sepia ink — the earth register.
    wash: [0.42, 0.33, 0.24],
    inkShift: [0.44, 0.27, 0.15],
    displayName: 'the mushroom floor',
  },
}

export const BIOME_IDS = Object.keys(BIOMES) as BiomeId[]

/** Largest predator radius multiplier across biomes — used for conservative
 *  spawn-placement clearance regardless of where the predator lands. */
export function maxPredatorRadiusMult(): number {
  let max = 1
  for (const id of BIOME_IDS) max = Math.max(max, BIOMES[id].predatorMods().radiusMult)
  return max
}

// ── Geometry ────────────────────────────────────────────────────────────────
// Concentric depth bands: shallow → deep → mushroom, each pair separated by
// a soft blend zone. The depth axis is worldDistanceAt — graph distance from
// the hub in the walled world (B2), radial distance from origin in open
// water — so the band radii below keep their tuned meanings either way.
//   biomeFactor  — the "descent" ramp shallow(0)→deep(1), SATURATES at 1 past
//                  deep (music/ambient/creature-flavor ride this; mushroom
//                  inherits the deep-descent treatment for free).
//   currentBiome — the biome IDENTITY at a point (drives spawns, palette, HUD).

/** Descent ramp [0,1]; 0 in shallow, 1 at/after the deep band. */
export function biomeFactorAt(x: number, y: number): number {
  const d = worldDistanceAt(x, y)
  return Math.max(0, Math.min(1, (d - T.biomes.inner) / (T.biomes.outer - T.biomes.inner)))
}

export function currentBiomeAt(x: number, y: number): BiomeId {
  const d = worldDistanceAt(x, y)
  const deepMid = (T.biomes.inner + T.biomes.outer) / 2
  const mushMid = (T.biomes.mushroomInner + T.biomes.mushroomOuter) / 2
  if (d >= mushMid) return 'mushroom'
  return d >= deepMid ? 'deep' : 'shallow'
}

function lerp3(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
}

/**
 * Background wash+ink at a point, plus the shallow→deep strength. The wash
 * COLOR is the descent color (deep by default, morphing to mushroom past the
 * deep band); the STRENGTH ramps it in over the shallow→deep blend. For a
 * shallow/deep-only world this returns exactly the former hardcoded values.
 */
export function biomeBackgroundAt(x: number, y: number): { wash: Rgb; ink: Rgb; strength: number } {
  const d = worldDistanceAt(x, y)
  const strength = biomeFactorAt(x, y)
  const mi = T.biomes.mushroomInner, mo = T.biomes.mushroomOuter
  let wash = BIOMES.deep.wash
  let ink = BIOMES.deep.inkShift
  if (d >= mo) {
    wash = BIOMES.mushroom.wash; ink = BIOMES.mushroom.inkShift
  } else if (d > mi) {
    const t = (d - mi) / (mo - mi)
    wash = lerp3(BIOMES.deep.wash, BIOMES.mushroom.wash, t)
    ink = lerp3(BIOMES.deep.inkShift, BIOMES.mushroom.inkShift, t)
  }
  return { wash, ink, strength }
}

export function biomeFactor(player: Entity): number {
  return biomeFactorAt(player.position.x, player.position.y)
}

export function currentBiome(p: Entity): BiomeId {
  return currentBiomeAt(p.position.x, p.position.y)
}
